// File: components/renderer/allies/drawMira.ts
import { Ally } from "../../types/entities";
import { drawHighFidelityFace } from "../utils";
import { CostumeId } from "../../../types";

// --- WEAPON RENDERER ---
const drawGokdo = (ctx: CanvasRenderingContext2D) => {
    ctx.save();
    // Angle the glaive
    ctx.rotate(Math.PI / 12);

    // Shaft (Long, segmented, gold)
    const goldGrad = ctx.createLinearGradient(-2, 0, 2, 0);
    goldGrad.addColorStop(0, "#b8860b"); goldGrad.addColorStop(0.5, "#ffd700"); goldGrad.addColorStop(1, "#b8860b");

    ctx.fillStyle = goldGrad;
    // Main shaft length approx 140px
    ctx.fillRect(-2, -70, 4, 140);

    // Segments (Spirals)
    ctx.strokeStyle = "#8b4513"; ctx.lineWidth = 0.5;
    for (let i = 0; i < 14; i++) {
        ctx.beginPath(); ctx.moveTo(-2, -60 + i * 10); ctx.lineTo(2, -65 + i * 10); ctx.stroke();
    }

    // Pommel (Golden)
    ctx.beginPath(); ctx.arc(0, 70, 4, 0, Math.PI * 2); ctx.fill();

    // -- BLADE HEAD --
    ctx.translate(0, -70);

    // Blade Base / Guard
    ctx.fillStyle = goldGrad;
    ctx.beginPath(); ctx.arc(0, 0, 6, 0, Math.PI * 2); ctx.fill();

    // Bells (Hanging from base)
    const drawBell = (bx: number, by: number) => {
        ctx.fillStyle = "#ffd700";
        ctx.beginPath(); ctx.arc(bx, by, 3, 0, Math.PI * 2); ctx.fill();
        ctx.strokeStyle = "#b8860b"; ctx.lineWidth = 1;
        ctx.beginPath(); ctx.moveTo(0, 0); ctx.lineTo(bx, by); ctx.stroke();
    };
    drawBell(-8, 5);
    drawBell(8, 5);

    // Blade (Curved Moon Sword, Translucent Blue)
    const bladeGrad = ctx.createLinearGradient(0, 0, 0, -80);
    bladeGrad.addColorStop(0, "rgba(76, 201, 240, 0.9)");
    bladeGrad.addColorStop(0.6, "rgba(67, 97, 238, 0.5)");
    bladeGrad.addColorStop(1, "rgba(255, 255, 255, 0.2)");

    ctx.fillStyle = bladeGrad;
    ctx.shadowColor = "#4cc9f0"; ctx.shadowBlur = 10;

    ctx.beginPath();
    ctx.moveTo(-4, 0); // Base left
    ctx.quadraticCurveTo(-20, -40, 0, -90); // Outer curve (Glaive shape)
    ctx.quadraticCurveTo(10, -40, 4, 0); // Inner curve
    ctx.closePath();
    ctx.fill();

    // Wavy Patterns on Blade
    ctx.strokeStyle = "rgba(255, 255, 255, 0.6)"; ctx.lineWidth = 1; ctx.shadowBlur = 0;
    ctx.beginPath();
    ctx.moveTo(-5, -20); ctx.quadraticCurveTo(0, -30, -5, -40);
    ctx.moveTo(-2, -50); ctx.quadraticCurveTo(3, -60, -2, -70);
    ctx.stroke();

    ctx.restore();
};

export const drawMira = (ctx: CanvasRenderingContext2D, mira: Ally, costume: CostumeId = 'DEFAULT', hideWeapon: boolean = false) => {
    ctx.save();
    ctx.translate(mira.x + mira.width / 2, mira.y + mira.height);
    const scale = mira.height / 180;
    ctx.scale(scale, scale);

    const bob = Math.sin(Date.now() / 220 + 1) * 2;
    ctx.translate(0, bob);

    const SKIN_TONE = "#ffebd6";
    const skinGrad = ctx.createLinearGradient(0, -180, 0, 0);
    skinGrad.addColorStop(0, SKIN_TONE); skinGrad.addColorStop(1, "#e0c4b0");

    // HAIR CONFIGURATION: Soft Pink Palette
    const hairGrad = ctx.createLinearGradient(0, -180, 0, 100);
    hairGrad.addColorStop(0, "#fbcfe8"); // Pink-200
    hairGrad.addColorStop(0.5, "#f472b6"); // Pink-400
    hairGrad.addColorStop(1, "#db2777"); // Pink-600

    // Slower swing animation
    const swingSpeed = 1200;
    // Synchronized swing: using time as base for both sides
    const pigtailSwayBase = (mira.isAttacking ? (Math.sin(mira.attackTimer * 0.3) * 35) : (Math.sin(Date.now() / swingSpeed) * 8));

    // --- 1. DRAW HAIR BEHIND EVERYTHING (Body and Face) ---
    ctx.save();
    ctx.translate(0, -150);

    // UNIFIED LONG HAIR - Single connected mass flowing from crown to knees
    const drawUnifiedLongHair = () => {
        ctx.save();
        ctx.fillStyle = hairGrad;
        const hairSway = pigtailSwayBase * 0.7;

        // Single unified path for all hair
        ctx.beginPath();

        // Start at the top center of the head (narrower at crown)
        ctx.moveTo(0, -45);

        // Crown hugs head more tightly, then gradually widens
        ctx.quadraticCurveTo(18, -42, 22, -30);

        // Transition from head to flowing hair
        ctx.quadraticCurveTo(28, -10, 32 + hairSway, 20);

        // Right side flows down the body to knee level (shortened)
        ctx.bezierCurveTo(34 + hairSway, 45, 28 + hairSway, 75, 22 + hairSway, 105);

        // Bottom right tip (knee level)
        ctx.quadraticCurveTo(16 + hairSway, 115, 6, 110 + hairSway);

        // Inner curve back up (between the "curtain")
        ctx.quadraticCurveTo(0, 100 + hairSway, -6, 110 + hairSway);

        // Bottom left tip (knee level)
        ctx.quadraticCurveTo(-16 + hairSway, 115, -22 + hairSway, 105);

        // Left side flows up back to head
        ctx.bezierCurveTo(-28 + hairSway, 75, -34 + hairSway, 45, -32 + hairSway, 20);

        // Transition back up to crown
        ctx.quadraticCurveTo(-28, -10, -22, -30);

        // Crown curves back to top center (narrower)
        ctx.quadraticCurveTo(-18, -42, 0, -45);

        ctx.closePath();
        ctx.fill();

        // Hair highlights/strands for texture
        ctx.strokeStyle = "rgba(255,255,255,0.12)";
        ctx.lineWidth = 2;
        ctx.beginPath();
        // Left strand
        ctx.moveTo(-12 + hairSway, 0);
        ctx.quadraticCurveTo(-16 + hairSway, 60, -12 + hairSway, 120);
        // Center-left strand
        ctx.moveTo(-4 + hairSway, 10);
        ctx.quadraticCurveTo(-6 + hairSway, 70, -4 + hairSway, 125);
        // Center-right strand
        ctx.moveTo(4 + hairSway, 10);
        ctx.quadraticCurveTo(6 + hairSway, 70, 4 + hairSway, 125);
        // Right strand
        ctx.moveTo(12 + hairSway, 0);
        ctx.quadraticCurveTo(16 + hairSway, 60, 12 + hairSway, 120);
        ctx.stroke();

        ctx.restore();
    };
    drawUnifiedLongHair();

    // Hair bands (decorative yellow ties near the head) - positioned for narrower hair
    ctx.fillStyle = "#facc15";
    ctx.beginPath(); ctx.roundRect(-18, -32, 12, 7, 2); ctx.fill();
    ctx.beginPath(); ctx.roundRect(6, -32, 12, 7, 2); ctx.fill();

    ctx.restore();

    // --- 2. BODY RENDERING (In front of hair) ---
    if (costume === 'DEFAULT') {
        // Upper Thighs (Skin)
        ctx.fillStyle = skinGrad;
        ctx.beginPath();
        ctx.moveTo(-13, -85); ctx.lineTo(13, -85);
        ctx.lineTo(10, -50); ctx.lineTo(-10, -50);
        ctx.fill();

        // Boots (Dark Brown/Black)
        const BOOT_COLOR = "#2a1a2a";
        ctx.fillStyle = BOOT_COLOR;

        ctx.beginPath(); ctx.moveTo(-12, -60); ctx.lineTo(-2, -60); ctx.lineTo(-4, 0); ctx.lineTo(-14, 0); ctx.fill();
        ctx.beginPath(); ctx.moveTo(2, -60); ctx.lineTo(12, -60); ctx.lineTo(14, 0); ctx.lineTo(4, 0); ctx.fill();

        // Details
        ctx.fillStyle = "#fff";
        ctx.beginPath(); ctx.moveTo(2, -60); ctx.lineTo(8, -60); ctx.lineTo(6, -30); ctx.lineTo(3, -30); ctx.fill();
        ctx.strokeStyle = "#fff"; ctx.lineWidth = 1;
        ctx.beginPath(); ctx.moveTo(-10, -55); ctx.lineTo(-8, -25); ctx.stroke();

        ctx.fillStyle = "#dc2626"; // Red Knee Pads
        const drawKneePad = (x: number) => {
            ctx.beginPath(); ctx.roundRect(x, -45, 9, 14, 2); ctx.fill();
        };
        drawKneePad(-13); drawKneePad(3);

        ctx.fillStyle = "#ffb300"; // Skirt
        ctx.beginPath(); ctx.moveTo(-15, -85); ctx.lineTo(15, -85); ctx.lineTo(20, -65); ctx.lineTo(-20, -65); ctx.fill();

        // Torso
        ctx.fillStyle = skinGrad; ctx.fillRect(-12, -100, 24, 15);
        ctx.fillStyle = "#111"; ctx.beginPath(); ctx.roundRect(-16, -145, 32, 45, 2); ctx.fill();

        // Graphic Print
        ctx.save();
        ctx.clip(new Path2D("M -16 -145 L 16 -145 L 16 -100 L -16 -100 Z"));
        ctx.fillStyle = "#3b82f6"; ctx.beginPath();
        ctx.moveTo(-16, -105); ctx.lineTo(-8, -120); ctx.lineTo(0, -112); ctx.lineTo(8, -125); ctx.lineTo(16, -110); ctx.lineTo(16, -95); ctx.lineTo(-16, -95); ctx.fill();
        ctx.fillStyle = "#ff69b4"; ctx.font = "bold 5px Arial"; ctx.textAlign = "center";
        ctx.fillText("WON'T", 0, -132); ctx.fillText("MISS", 0, -126);
        ctx.restore();
    }
    else if (costume === 'PAJAMAS') {
        // Polar Bear Sweater
        ctx.fillStyle = "#ecf0f1"; ctx.beginPath(); ctx.roundRect(-20, -145, 40, 95, 6); ctx.fill();
        ctx.fillStyle = "#333";
        ctx.fillRect(-8, -125, 6, 2); ctx.fillRect(2, -125, 6, 2);
        ctx.beginPath(); ctx.arc(0, -118, 2, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = "#ff1493"; // Bow
        ctx.beginPath(); ctx.moveTo(10, -140); ctx.lineTo(18, -148); ctx.lineTo(18, -132); ctx.closePath(); ctx.fill();
        ctx.beginPath(); ctx.moveTo(10, -140); ctx.lineTo(2, -148); ctx.lineTo(2, -132); ctx.closePath(); ctx.fill();
        ctx.beginPath(); ctx.arc(10, -140, 2, 0, Math.PI * 2); ctx.fill();

        // Paw Prints
        ctx.fillStyle = "#3498db";
        const drawPaw = (px: number, py: number) => {
            ctx.beginPath(); ctx.arc(px, py, 4, 0, Math.PI * 2); ctx.fill();
        };
        drawPaw(-12, -70); drawPaw(12, -70);

        // Legs
        ctx.fillStyle = skinGrad; ctx.fillRect(-10, -50, 8, 45); ctx.fillRect(2, -50, 8, 45);
        ctx.fillStyle = "#ffb6c1"; // Gingham Socks
        ctx.fillRect(-10, -20, 8, 15); ctx.fillRect(2, -20, 8, 15);
        ctx.fillStyle = "#f06292"; // Crocs
        ctx.beginPath(); ctx.roundRect(-14, -5, 12, 10, 4); ctx.fill();
        ctx.beginPath(); ctx.roundRect(2, -5, 12, 10, 4); ctx.fill();
    }
    else if (costume === 'LEATHER') {
        ctx.fillStyle = "#111"; ctx.fillRect(-12, -90, 10, 90); ctx.fillRect(2, -90, 10, 90);
        ctx.fillStyle = "#000"; ctx.fillRect(-16, -145, 32, 55);
        ctx.fillStyle = "rgba(255,255,255,0.3)"; ctx.beginPath(); ctx.moveTo(-16, -145); ctx.lineTo(-6, -145); ctx.lineTo(-16, -100); ctx.fill();
    }
    else if (costume === 'HANBOK') {
        ctx.fillStyle = "#fce7f3"; ctx.beginPath(); ctx.moveTo(0, -120); ctx.lineTo(-40, 0); ctx.lineTo(40, 0); ctx.fill();
        ctx.fillStyle = "#db2777"; ctx.fillRect(-15, -150, 30, 30);
    }

    // --- 3. DYNAMIC ARMS & WEAPON ---
    ctx.save();
    const armColor = (costume === 'PAJAMAS') ? "#3498db" : skinGrad;
    ctx.fillStyle = armColor;

    // Left Arm
    ctx.save();
    if (mira.isAttacking) {
        const swingProgress = 1 - (mira.attackTimer / 15);
        const angle = 0.3 + (Math.sin(swingProgress * Math.PI) * 1.5);
        ctx.translate(-16, -145);
        ctx.rotate(angle);
    } else {
        ctx.translate(-16, -145);
        ctx.rotate(0.3);
    }
    ctx.beginPath(); ctx.roundRect(-6, 0, 12, 45, 4); ctx.fill();
    ctx.restore();

    // Weapon (Gokdo)
    ctx.save();
    if (mira.isAttacking) {
        const swingProgress = 1 - (mira.attackTimer / 15);
        const angle = 0.3 + (Math.sin(swingProgress * Math.PI) * 1.5);
        ctx.translate(-16, -145); ctx.rotate(angle); ctx.translate(16, 145);
        ctx.translate(10 + (swingProgress * 40), -110 + (Math.sin(swingProgress * Math.PI) * 20));
        ctx.rotate(swingProgress * 1.0);
    } else {
        ctx.translate(10, -110);
    }
    if (!hideWeapon) {
        drawGokdo(ctx);
    }
    ctx.restore();

    // Right Arm
    ctx.save();
    ctx.translate(16, -145);
    if (mira.isAttacking) ctx.rotate(-0.8);
    else ctx.rotate(-0.3);
    ctx.beginPath(); ctx.roundRect(-6, 0, 12, 45, 4); ctx.fill();
    ctx.restore();
    ctx.restore();

    // --- 4. HEAD RENDERING (On top of body) ---
    ctx.save();
    ctx.translate(0, -150);

    // Neck (Skin tone)
    ctx.fillStyle = skinGrad;
    ctx.fillRect(-4, -5, 8, 12);

    if (costume === 'DEFAULT') {
        ctx.fillStyle = "#111"; ctx.fillRect(-4, 3, 8, 3); // Choker
        ctx.fillStyle = "#fbbf24"; ctx.beginPath(); ctx.arc(0, 4.5, 1.5, 0, Math.PI * 2); ctx.fill();
    }

    // Face - Mira has dark brown almond-shaped eyes
    drawHighFidelityFace(ctx, skinGrad, "#2a1a11", hairGrad);

    // Front bangs overlay
    ctx.fillStyle = hairGrad;
    ctx.beginPath();
    ctx.moveTo(-12, -40);
    ctx.quadraticCurveTo(0, -52, 12, -40);
    ctx.lineTo(12, -32);
    ctx.quadraticCurveTo(0, -42, -12, -32);
    ctx.fill();

    ctx.restore();

    ctx.restore();
};